import { useMemo } from 'react';

import { useQuery } from '@mint-lib/query-client';
import { Metadata } from '@mint-sdk/api-mdm';

import type {
  PlatformByLabel,
  UseMdmPlatformsOptions,
  UseMdmPlatformsResult,
} from './useMdmPlatforms.types.js';
import { useMdmPlatformType } from './useMdmPlatformType.js';
import { useService } from './useService.js';

export const useMdmPlatformsV2 = (
  options: UseMdmPlatformsOptions,
): UseMdmPlatformsResult => {
  const { companyId, context = 'integration', onlyOmni = false } = options;

  const [queryClient] = useService({
    name: 'queryClient',
    suspense: true,
  });

  const { data: platformsData, isLoading: isPlatformsDataLoading } = useQuery(
    {
      ...Metadata.getContextMetadata({
        params: {
          fieldUrnIds: ['platform_v2'],
          companyId,
          context,
        },
      }),
      enabled: typeof companyId === 'number',
    },
    queryClient,
  );

  const {
    isOmniPlatform,
    isAdServerPlatform,
    isAnalyticsPlatform,
    isLoading: isPlatformTypeLoading,
  } = useMdmPlatformType({
    enabled: onlyOmni,
  });

  const platforms = useMemo(() => {
    const platformOptions =
      platformsData?.[
        Object.keys(platformsData).find((key) => key.includes('platform_v2')) ??
          ''
      ]?.options ?? [];

    if (!onlyOmni) {
      return platformOptions;
    }

    return platformOptions
      .filter(
        (platform) =>
          isOmniPlatform(platform.value) ||
          isAdServerPlatform(platform.value) ||
          isAnalyticsPlatform(platform.value),
      )
      .map((platform) => ({ ...platform, platform: platform.value }));
  }, [
    platformsData,
    isOmniPlatform,
    isAdServerPlatform,
    isAnalyticsPlatform,
    onlyOmni,
  ]);

  const platformByLabel = useMemo(
    () =>
      (platforms ?? []).reduce(
        (acc: PlatformByLabel, item) => ({
          ...acc,
          ...(item ? { [item.value]: item } : null),
        }),
        {},
      ),
    [platforms],
  );

  return {
    platforms,
    platformByLabel,
    isLoading: isPlatformsDataLoading || (onlyOmni && isPlatformTypeLoading),
  };
};
